import React, { useMemo } from 'react';
import VirtualSection from '../sessionSections/virtualSession/VirtualSection';
import OnDemandSection from '../sessionSections/virtualSession/OnDemandSection';
import { SessionPageInfo } from '../SessionPageInfo';
import FeatureToggleUtils from 'FeatureToggleUtils';
import { getIsVirtualSectionEnabled, getIsOnDemandSectionEnabled } from '../sessionBroadcastUtils';
import classnames from 'classnames';
import SessionControlButtons from '../sessionSections/SessionControlButtons';

const useOnDemandProps = ({
    siteConf,
    eventUrls,
    session,
    me,
    showEmailLogin,
    event,
    scrollToBroadcast,
    setScrollToSessionBroadcastAction,
    setLoginV2OffsetTop,
    ticketsSelectionData,
}) =>
    useMemo(
        () => ({
            siteConf,
            eventUrls,
            session,
            me,
            showEmailLogin,
            event,
            scrollToBroadcast,
            setScrollToSessionBroadcastAction,
            setLoginV2OffsetTop,
            ticketsSelectionData,
        }),
        [
            siteConf,
            eventUrls,
            session,
            me,
            showEmailLogin,
            event,
            scrollToBroadcast,
            setScrollToSessionBroadcastAction,
            setLoginV2OffsetTop,
            ticketsSelectionData,
        ]
    );

const useVirtualSectionProps = ({
    siteConf,
    eventUrls,
    session,
    me,
    sessionViewType,
    showEmailLogin,
    event,
    setVirtualSessionDetails,
    currentVirtualSessionDetails,
    scrollToBroadcast,
    setScrollToSessionBroadcastAction,
    setRecordUrl,
    recordUrl,
    setLoginV2SpinnerOrigin,
    loginV2LoadingState,
    isWebLoginV2Used,
    setLoginV2OffsetTop,
    ticketsSelectionData,
    isPreliminaryAccessEnabled,
}) =>
    useMemo(
        () => ({
            siteConf,
            eventUrls,
            session,
            me,
            sessionViewType,
            showEmailLogin,
            event,
            setVirtualSessionDetails,
            currentVirtualSessionDetails,
            scrollToBroadcast,
            setScrollToSessionBroadcastAction,
            setRecordUrl,
            recordUrl,
            setLoginV2SpinnerOrigin,
            loginV2LoadingState,
            isWebLoginV2Used,
            setLoginV2OffsetTop,
            ticketsSelectionData,
            isPreliminaryAccessEnabled,
        }),
        [
            siteConf,
            eventUrls,
            session,
            me,
            sessionViewType,
            showEmailLogin,
            event,
            setVirtualSessionDetails,
            currentVirtualSessionDetails,
            scrollToBroadcast,
            setScrollToSessionBroadcastAction,
            setRecordUrl,
            recordUrl,
            setLoginV2SpinnerOrigin,
            loginV2LoadingState,
            isWebLoginV2Used,
            setLoginV2OffsetTop,
            ticketsSelectionData,
            isPreliminaryAccessEnabled,
        ]
    );

const useSessionInfoProps = ({
    isRingAvailable,
    session,
    me,
    settings,
    eventUrls,
    showEmailLogin,
    toggleMarker,
    sessionViewType,
    toggleSessionRegistrationStatusPerSession,
    openCloseTicketSelectionPopUp,
    ticketsSelectionData,
    siteConf,
    setSessionFullRegistration,
    setLoginV2OffsetTop,
    isSessionRegistrationEnabled,
    event,
    setVirtualSessionDetails,
    setRecordUrl,
    setScrollToSessionBroadcastAction,
    showRegisterAndLoginModal,
    setLoginV2SpinnerOrigin,
    loginV2LoadingState,
    isWebLoginV2Used,
    accessErrorType,
    shouldUseCompactDescription,
    topOfSessionViewRef,
    rate,
    feedback,
    sendRate,
}) =>
    useMemo(
        () => ({
            isRingAvailable,
            session,
            me,
            settings,
            eventUrls,
            showEmailLogin,
            toggleMarker,
            sessionViewType,
            toggleSessionRegistrationStatusPerSession,
            openCloseTicketSelectionPopUp,
            ticketsSelectionData,
            siteConf,
            setSessionFullRegistration,
            setLoginV2OffsetTop,
            isSessionRegistrationEnabled,
            event,
            setVirtualSessionDetails,
            setRecordUrl,
            setScrollToSessionBroadcastAction,
            showRegisterAndLoginModal,
            setLoginV2SpinnerOrigin,
            loginV2LoadingState,
            isWebLoginV2Used,
            accessErrorType,
            shouldUseCompactDescription,
            topOfSessionViewRef,
            rate,
            feedback,
            sendRate,
        }),
        [
            isRingAvailable,
            session,
            me,
            settings,
            eventUrls,
            showEmailLogin,
            toggleMarker,
            sessionViewType,
            toggleSessionRegistrationStatusPerSession,
            openCloseTicketSelectionPopUp,
            ticketsSelectionData,
            siteConf,
            setSessionFullRegistration,
            setLoginV2OffsetTop,
            setRecordUrl,
            setScrollToSessionBroadcastAction,
            showRegisterAndLoginModal,
            setLoginV2SpinnerOrigin,
            loginV2LoadingState,
            isWebLoginV2Used,
            accessErrorType,
            shouldUseCompactDescription,
            topOfSessionViewRef,
            rate,
            feedback,
            sendRate,
            isSessionRegistrationEnabled,
            setVirtualSessionDetails,
            event
        ]
    );

const useVirtualSectionElement = (props) => {
    const { isRingAvailable, session, event, sessionViewType, isDynamicSizeEnabled } = props;
    const isVirtualSectionEnabled = getIsVirtualSectionEnabled(session, event, sessionViewType);
    const isOnDemandSectionEnabled = getIsOnDemandSectionEnabled(session, event, sessionViewType);
    const onDemandProps = useOnDemandProps(props);
    const virtualSectionProps = useVirtualSectionProps(props);

    return useMemo(() => {
        if (isRingAvailable || (!isOnDemandSectionEnabled && !isVirtualSectionEnabled)) {
            return null;
        }

        return (
            <div className={classnames('full-width-video session-page-element', { 'container margin-bottom-small': !isDynamicSizeEnabled })}>
                {isVirtualSectionEnabled && <VirtualSection {...virtualSectionProps} />}
                {isOnDemandSectionEnabled && <OnDemandSection {...onDemandProps} />}
            </div>
        );
    }, [isRingAvailable, virtualSectionProps, onDemandProps, isVirtualSectionEnabled, isOnDemandSectionEnabled, isDynamicSizeEnabled]);
};

const useSessionInfoElement = (props) => {
    const sessionInfoProps = useSessionInfoProps(props);
    const { pageElementClasses, displayInContainer, showControlPanel, setVirtualSessionDetails, isDynamicSizeEnabled } = props;

    return useMemo(() => <div
        className='session-info-container-wrapper'>
        <SessionControlButtons
            showControlPanel={showControlPanel}
            setVirtualSessionDetails={setVirtualSessionDetails}
        />
        <div className={classnames(pageElementClasses, 'margin-bottom-small', { 'container': displayInContainer && !isDynamicSizeEnabled })}>
            <SessionPageInfo
                {...sessionInfoProps}
                shouldUseCompactDescription={sessionInfoProps.shouldUseCompactDescription || isDynamicSizeEnabled}
            />
        </div>
    </div>, [
        showControlPanel,
        setVirtualSessionDetails,
        sessionInfoProps,
        pageElementClasses,
        displayInContainer,
        isDynamicSizeEnabled
    ]);
};

export const useSessionElements = (props) => {
    const virtualSectionElement = useVirtualSectionElement(props);
    const sessionInfoElement = useSessionInfoElement(props);

    return {
        virtualSectionElement,
        sessionInfoElement,
    };
};
